import { z } from 'zod'

/**
 * A tenant user's role within one tenant. Mirrors `TenantRole` in
 * `api/prisma/schema.prisma`.
 *
 * - `owner`: everything, including the team and live keys.
 * - `developer`: integration work (test keys, settings, logs) and envelopes.
 * - `viewer`: read-only envelopes and logs; no downloads or actions.
 */
export const TENANT_ROLES = ['owner', 'developer', 'viewer'] as const

export const tenantRoleSchema = z.enum(TENANT_ROLES)

export type TenantRole = z.infer<typeof tenantRoleSchema>

export const PRISMA_TENANT_ROLES = ['OWNER', 'DEVELOPER', 'VIEWER'] as const

export type PrismaTenantRole = (typeof PRISMA_TENANT_ROLES)[number]

export const TENANT_ROLE_FROM_PRISMA = {
  OWNER: 'owner',
  DEVELOPER: 'developer',
  VIEWER: 'viewer',
} as const satisfies Record<PrismaTenantRole, TenantRole>

export const TENANT_ROLE_TO_PRISMA = {
  owner: 'OWNER',
  developer: 'DEVELOPER',
  viewer: 'VIEWER',
} as const satisfies Record<TenantRole, PrismaTenantRole>

/** How a role is named in mail, the dashboard, and MCP confirmations. */
export const TENANT_ROLE_LABELS = {
  owner: 'Owner',
  developer: 'Developer',
  viewer: 'Viewer',
} as const satisfies Record<TenantRole, string>

export const PRISMA_TENANT_ROLE_LABELS = {
  OWNER: TENANT_ROLE_LABELS.owner,
  DEVELOPER: TENANT_ROLE_LABELS.developer,
  VIEWER: TENANT_ROLE_LABELS.viewer,
} as const satisfies Record<PrismaTenantRole, string>
